import React from 'react';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Chip,
  Typography,
  Grid,
  Box,
  Switch,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import SendIcon from '@mui/icons-material/Send';
import { GENRES, lexileRanges, type GradeLevel } from './assessmentsFormatUtils';
import type { Standard } from './assessmentsTypes';

export interface AssessmentFormProps {
  handleSubmit: (e: React.FormEvent) => void;
  gradeLevel: string;
  handleGradeLevelChange: (event: SelectChangeEvent) => void;
  loadingGradeLevels: boolean;
  availableGradeLevels: string[];
  selectedStrand: string;
  setSelectedStrand: (value: string) => void;
  setSelectedStandards: (value: string[]) => void;
  availableStrands: string[];
  strandFilteredStandards: Standard[];
  selectedStandards: string[];
  standardLoading: boolean;
  capitalizeFirstLetter: (str: string | undefined | null) => string;
  topic: string;
  setTopic: (value: string) => void;
  language: 'en' | 'es';
  setLanguage: (value: 'en' | 'es') => void;
  isPairedPassage: boolean;
  setIsPairedPassage: (value: boolean) => void;
  paragraphCount: number;
  setParagraphCount: (value: number) => void;
  getDefaultParagraphCount: (grade: string) => number;
  genre: string;
  setGenre: (value: string) => void;
  genreTwo: string;
  setGenreTwo: (value: string) => void;
  revisingAndEditing: boolean;
  setRevisingAndEditing: (value: boolean) => void;
  generateQuestions: boolean;
  setGenerateQuestions: (value: boolean) => void;
  questionStyle: string;
  setQuestionStyle: (value: string) => void;
  questionMix: string;
  setQuestionMix: (value: string) => void;
  isStreaming: boolean;
  isLoading: boolean;
  generateImages: boolean;
  setGenerateImages: (value: boolean) => void;
  imageCount: number;
  setImageCount: (value: number) => void;
}

const AssessmentForm: React.FC<AssessmentFormProps> = (props) => {
  const {
    handleSubmit,
    gradeLevel,
    handleGradeLevelChange,
    loadingGradeLevels,
    availableGradeLevels,
    selectedStrand,
    setSelectedStrand,
    setSelectedStandards,
    availableStrands,
    strandFilteredStandards,
    selectedStandards,
    standardLoading,
    capitalizeFirstLetter,
    topic,
    setTopic,
    language,
    setLanguage,
    isPairedPassage,
    setIsPairedPassage,
    paragraphCount,
    setParagraphCount,
    getDefaultParagraphCount,
    genre,
    setGenre,
    genreTwo,
    setGenreTwo,
    revisingAndEditing,
    setRevisingAndEditing,
    generateQuestions,
    setGenerateQuestions,
    questionStyle,
    setQuestionStyle,
    questionMix,
    setQuestionMix,
    isStreaming,
    isLoading,
    generateImages,
    setGenerateImages,
    imageCount,
    setImageCount,
  } = props;

  return (
<form onSubmit={handleSubmit}>
  
  {/* Two-column form layout */}
  <Grid container spacing={3} sx={{ mb: 3 }}>
    <Grid item xs={12} md={6}>
      {/* Step 1: Basic Settings */}
      <Box sx={{ bgcolor: 'grey.50', border: '1px solid #e5e7eb', borderRadius: 2, p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2563EB', mb: 2 }}>
          Step 1: Basic Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Grade Level *</InputLabel>
              <Select
                value={gradeLevel}
                onChange={handleGradeLevelChange}
                label="Grade Level *"
                required
              >
                {loadingGradeLevels ? (
                  <MenuItem value="" disabled><em>Loading grade levels...</em></MenuItem>
                ) : availableGradeLevels.length === 0 ? (
                  <MenuItem value="" disabled><em>No grade levels available</em></MenuItem>
                ) : (
                  availableGradeLevels.map((grade) => (
                    <MenuItem key={grade} value={grade}>
                      {['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].includes(grade)
                        ? `Grade ${grade === 'K' ? 'K' : grade}`
                        : grade}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
              Lexile: {lexileRanges[gradeLevel as GradeLevel]}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Content Strand</InputLabel>
              <Select
                value={selectedStrand}
                onChange={(e) => {
                  setSelectedStrand(e.target.value);
                  // Clear selected standards when strand changes
                  setSelectedStandards([]);
                }}
                label="Content Strand"
              >
                <MenuItem value="All">All Strands</MenuItem>
                {availableStrands.map((strand) => (
                  <MenuItem key={strand} value={strand}>
                    {strand}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              options={strandFilteredStandards}
              getOptionLabel={(option) => {
                if (!option) return '';
                const code = option.standard || option.code || 'N/A';
                const desc = option.description || option.short_description || 'Standard';
                return `${code} - ${capitalizeFirstLetter(desc)}`;
              }}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option._id === value._id;
              }}
              value={strandFilteredStandards.filter(s => selectedStandards.includes(s._id))}
              onChange={(event, newValue) => {
                setSelectedStandards(newValue.map(s => s._id));
              }}
              disableCloseOnSelect
              fullWidth
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Learning Standards (Optional)"
                  placeholder={selectedStandards.length === 0 ? "Search for standards..." : ""}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {standardLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option._id}
                    label={option.code || option.standard}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: '20px' }}
                  />
                ))
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* Step 2: Content */}
      <Box sx={{ bgcolor: 'grey.50', border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2563EB', mb: 2 }}>
          Step 2: Content
        </Typography>
        <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            label="Topic (optional)"
            placeholder="Leave blank for a random, grade-appropriate topic"
            size="small"
            fullWidth
            helperText="Enter the main theme, or leave blank to generate a random topic."
          />
        </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Language"
              value={language}
              onChange={(e) => setLanguage(e.target.value as 'en' | 'es')}
              SelectProps={{ native: true }}
              helperText="Language of passage, questions, and answer key"
            >
              <option value="en">English</option>
              <option value="es">Spanish (Beta)</option>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={isPairedPassage} onChange={(e) => setIsPairedPassage(e.target.checked)} size="small" />}
              label="Generate two related passages for comparison questions"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' }, mt: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              type="number"
              value={paragraphCount}
              onChange={(e) => setParagraphCount(parseInt(e.target.value) || 4)}
              label="Paragraph Count"
              placeholder="4"
              size="small"
              fullWidth
              inputProps={{ min: 1, max: 10 }}
              helperText={`Recommended for Grade ${gradeLevel}: ${getDefaultParagraphCount(gradeLevel)} paragraphs`}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              disablePortal
              options={GENRES}
              value={genre}
              onChange={(_, val) => setGenre(val || '')}
              renderInput={(params) => (
                <TextField {...params} label={isPairedPassage ? 'First Genre *' : 'Genre *'} size="medium" required />
              )}
            />
          </Grid>
          {isPairedPassage && (
            <Grid item xs={12}>
              <Autocomplete
                disablePortal
                options={GENRES}
                value={genreTwo}
                onChange={(_, val) => setGenreTwo(val || '')}
                renderInput={(params) => (
                  <TextField {...params} label="Second Genre *" size="medium" required />
                )}
              />
            </Grid>
          )}

          {/* Revising and Editing Toggle */}
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={revisingAndEditing} onChange={(e) => setRevisingAndEditing(e.target.checked)} />}
              label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Writing (Revising and Editing)</Typography>}
            />
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: -1 }}>
              Number every sentence and focus questions on editing and revising.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Grid>

    <Grid item xs={12} md={6}>
      {/* Step 3: Assessment Options */}
      <Box sx={{ bgcolor: 'grey.50', border: '1px solid #e5e7eb', borderRadius: 2, p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2563EB', mb: 2 }}>
          Step 3: Assessment Options
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={generateQuestions} onChange={(e) => setGenerateQuestions(e.target.checked)} size="small" />} label="Generate Questions" sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem', fontWeight: 500 }, mb: 1 }} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={generateImages} onChange={(e) => setGenerateImages(e.target.checked)} size="small" />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                    Generate AI Illustrations (optional)
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                    Add educational images that match the passage content
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0 }}
            />
          </Grid>
          {generateImages && (
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                value={imageCount}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 1;
                  if (val >= 1 && val <= 5) setImageCount(val);
                }}
                label="Number of Images"
                size="small"
                fullWidth
                inputProps={{ min: 1, max: 5 }}
                helperText="Generate 1-5 illustrations (images generate in parallel for speed)"
                sx={{ mb: 1 }}
              />
            </Grid>
          )}
          {generateQuestions && (
            <>
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Question Style</InputLabel>
                      <Select value={questionStyle} onChange={(e: SelectChangeEvent) => setQuestionStyle(e.target.value)} label="Question Style">
                        <MenuItem value="StandardizedTest">Standardized Test Format</MenuItem>
                        <MenuItem value="ReadingComprehension">Reading Comprehension Format</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Question Types</InputLabel>
                      <Select value={questionMix} onChange={(e: SelectChangeEvent) => setQuestionMix(e.target.value)} label="Question Types">
                        <MenuItem value="multiple_choice">Multiple Choice Only</MenuItem>
                        <MenuItem value="mixed">Multiple Choice + Open Ended</MenuItem>
                        <MenuItem value="text_only">Text Questions Only</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              {questionStyle === 'StandardizedTest' && (
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                    Questions aligned to your state's standardized test format.
                  </Typography>
                </Grid>
              )}
              {/* Answer key is always included by default; hiding manual toggle */}
            </>
          )}
        </Grid>
      </Box>

      {/* CTA */}
      <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
        <Button type="submit" variant="contained" disabled={isStreaming || isLoading} startIcon={isStreaming ? <CircularProgress size={18} /> : <SendIcon />} size="large" sx={{ fontSize: '0.95rem', fontWeight: 700, py: 1.5, px: 4, minWidth: 220, borderRadius: 2 }}>
          {isStreaming ? 'Generating Passage...' : 'Generate Passage'}
        </Button>
      </Box>
    </Grid>
  </Grid>
</form>
  );
};

export default AssessmentForm;
